/* RBesigner.rt — scroll effects
   Two tiers, picked in theme.liquid before the page paints:
   - .rb-full (desktop): reveal + hero parallax + statement word reveal + ticker boost
   - .rb-lite (phones / low-power): reveal (fade only) + progress bar, nothing runs per scroll frame
   Nothing runs for visitors who prefer reduced motion. */
(() => {
  const root = document.documentElement;
  window.rbScrollFx = true;
  if (!root.classList.contains('rb-anim')) return;

  const LITE = root.classList.contains('rb-lite');

  // Phones reveal fewer, larger pieces: fewer elements to track and animate.
  const REVEAL = (LITE
    ? [
        '.rb-head',
        '.rb-values__item',
        '.rb-story__copy',
        '.rb-story__panel',
        '.rb-steps__item',
        '.rb-faq__head',
        '.rb-faq__list',
        '.rb-statement',
        '.rb-page-hero__inner',
        '.resource-list__item',
        '.rb-3d__copy',
      ]
    : [
        '.rb-head > *',
        '.rb-values__item',
        '.rb-story__copy > *',
        '.rb-story__image',
        '.rb-story__card',
        '.rb-story__list li',
        '.rb-steps__item',
        '.rb-faq__head > *',
        '.rb-faq__item',
        '.rb-statement > :not(p)',
        '.rb-page-hero__inner > *',
        '.resource-list__item',
        '.section-resource-list__header',
        '.rb-3d__copy > *',
      ]
  ).join(',');

  /* ---------- Reveal ---------- */
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }
    },
    { rootMargin: '0px 0px -6% 0px', threshold: LITE ? 0 : 0.12 }
  );

  function setupReveal(scope = document) {
    scope.querySelectorAll(REVEAL).forEach((el) => {
      if (el.dataset.rbReveal !== undefined) return;
      const sibs = [...el.parentElement.children].filter((c) => c.matches(REVEAL));
      el.style.setProperty('--rb-i', Math.min(sibs.indexOf(el), LITE ? 3 : 7));
      el.dataset.rbReveal = '';
      io.observe(el);
    });
  }

  // Anything the visitor jumped past (anchor link, back button, fast fling) gets shown.
  // Runs once after scrolling settles, not every frame.
  let settleTimer = 0;
  function revealPassed() {
    document.querySelectorAll('[data-rb-reveal]:not(.is-in)').forEach((el) => {
      if (el.getBoundingClientRect().bottom < 0) {
        el.classList.add('is-in');
        io.unobserve(el);
      }
    });
  }

  /* ---------- Statement word split (desktop only) ---------- */
  function splitWords(scope = document) {
    if (LITE) return;
    scope.querySelectorAll('.rb-statement p').forEach((p) => {
      if (p.dataset.rbSplit !== undefined) return;
      p.dataset.rbSplit = '';
      const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const frag = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) return frag.append(part);
          const s = document.createElement('span');
          s.className = 'rb-word';
          s.textContent = part;
          frag.append(s);
        });
        node.replaceWith(frag);
      });
    });
  }

  /* ---------- Progress bar ---------- */
  let bar = null;
  if (root.classList.contains('rb-progress')) {
    bar = document.createElement('div');
    bar.className = 'rb-progress-bar';
    bar.setAttribute('aria-hidden', 'true');
    document.body.prepend(bar);
  }

  /* ---------- Scroll loop ---------- */
  let maxScroll = 1;
  let vh = window.innerHeight || 1;
  const measure = () => {
    vh = window.innerHeight || 1;
    maxScroll = Math.max(document.documentElement.scrollHeight - vh, 1);
  };

  let lastY = window.scrollY;
  let velocity = 0;
  let ticking = false;

  function frame() {
    ticking = false;
    const y = window.scrollY;

    if (bar) bar.style.transform = `scaleX(${Math.min(y / maxScroll, 1).toFixed(4)})`;
    if (LITE) return; // phones stop here: one transform per frame, nothing else

    // hero parallax
    const hero = document.querySelector('.rb-hero');
    if (hero) {
      const r = hero.getBoundingClientRect();
      if (r.bottom > 0) {
        const p = Math.min(Math.max(-r.top / Math.max(r.height, 1), 0), 1);
        hero.style.setProperty('--rb-hero-p', p.toFixed(3));
      }
    }

    // statement words
    document.querySelectorAll('.rb-statement p[data-rb-split]').forEach((p) => {
      const r = p.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const start = vh * 0.9;
      const end = vh * 0.4;
      const prog = Math.min(Math.max((start - r.top) / (start - end), 0), 1);
      const words = p.querySelectorAll('.rb-word');
      const lit = prog * words.length;
      words.forEach((w, i) => w.style.setProperty('--rb-w', Math.min(Math.max(lit - i, 0), 1).toFixed(2)));
    });

    // ticker speeds up with scroll velocity, then eases back
    velocity = velocity * 0.85 + Math.abs(y - lastY) * 0.15;
    lastY = y;
    const track = document.querySelector('.rb-ticker__track');
    const a = track && track.getAnimations && track.getAnimations()[0];
    if (a) a.playbackRate = 1 + Math.min(velocity / 12, 4);
    if (velocity > 0.5) requestTick();
  }

  function requestTick() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(frame);
    }
  }

  function onScroll() {
    if (bar || !LITE) requestTick();
    clearTimeout(settleTimer);
    settleTimer = setTimeout(revealPassed, 150);
  }

  function init(scope) {
    splitWords(scope);
    setupReveal(scope);
    measure();
    requestTick();
  }

  init(document);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => { measure(); requestTick(); }, { passive: true });
  window.addEventListener('load', measure);

  // Product grids can be re-rendered (filters, pagination, theme editor)
  let pending = false;
  new MutationObserver((muts) => {
    if (pending || !muts.some((m) => m.addedNodes.length)) return;
    pending = true;
    setTimeout(() => { pending = false; init(document); }, 200);
  }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    e.target.querySelectorAll('[data-rb-reveal]').forEach((el) => el.classList.add('is-in'));
  });
})();
